"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_SHARD_DIRECTORY_PART_SUMMARY_KEYS = void 0;
exports.planDatabaseShardDirectoryParts = planDatabaseShardDirectoryParts;
exports.planDatabaseShardDirectoryHeadChanges = planDatabaseShardDirectoryHeadChanges;
exports.applyDatabaseShardDirectoryHeadChanges = applyDatabaseShardDirectoryHeadChanges;
exports.planDatabaseShardDirectoryWrite = planDatabaseShardDirectoryWrite;
exports.writeDatabaseShardDirectory = writeDatabaseShardDirectory;
exports.writeNewDatabaseShardDirectory = writeNewDatabaseShardDirectory;
const yjs_1 = require("yjs");
const database_row_shard_planning_1 = require("./database-row-shard-planning");
const database_shard_directory_parts_1 = require("./database-shard-directory-parts");
const database_shard_manifest_crdt_1 = require("./database-shard-manifest-crdt");
const database_storage_errors_1 = require("./database-storage-errors");
const index_1 = require("./index");
/**
 * Parts of `next` that keep the stored part boundaries: every shard stays in
 * its part, a new shard joins the part of the shard before it (the first part
 * when it leads), a part grown past `targetBytes` splits into even parts that
 * take the ids of emptied parts following it before new ids, and a part
 * without shards stays as an empty entry. Part ids are therefore never handed
 * out twice, and a write touches only the parts whose shards change.
 */
function planDatabaseShardDirectoryParts(current, next, targetBytes = database_shard_directory_parts_1.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES) {
    return planDirectoryParts(current, next, targetBytes);
}
/**
 * {@link planDatabaseShardDirectoryParts} over the parts of the head, of which
 * only those with shards were read: `next` holds the shards of the read
 * parts, and an unread part comes back as it was.
 */
function planDirectoryParts(current, next, targetBytes) {
    (0, index_1.assertDatabaseShardManifest)(next);
    const owner = new Map();
    current.forEach((part, index) => {
        for (const shard of part.shards ?? [])
            owner.set(shard.shardId, index);
    });
    const sections = current.map(() => []);
    if (!sections.length)
        sections.push([]);
    // A shard no read part held follows the shard before it, the first of
    // the first read part when it leads.
    let section = Math.max(0, current.findIndex(part => part.shards));
    for (const shard of next.shards) {
        // Never back: a shard moved before its part's predecessor stays in order.
        section = Math.max(section, owner.get(shard.shardId) ?? section);
        if (current[section] && !current[section].shards)
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard ${shard.shardId} follows no read directory part`);
        sections[section].push(shard);
    }
    let nextIndex = current.reduce((max, part) => Math.max(max, Number(part.partId)), 0);
    const parts = [];
    const part = (partId, shards) => parts.push({ databaseId: next.databaseId, partId, shards });
    const unread = (index) => current[index] && !current[index].shards;
    for (let index = 0; index < sections.length; index++) {
        const shards = sections[index];
        if (unread(index)) {
            parts.push(current[index]);
            continue;
        }
        if (!shards.length) {
            if (current[index])
                part(current[index].partId, []);
            continue;
        }
        const chunks = splitEvenly(shards, targetBytes);
        for (const [chunkIndex, chunk] of chunks.entries()) {
            if (chunkIndex === 0 && current[index]) {
                part(current[index].partId, chunk);
            }
            else if (current[index + 1] &&
                !unread(index + 1) &&
                !sections[index + 1].length) {
                // A split continues into the emptied part that follows it.
                part(current[++index].partId, chunk);
            }
            else {
                part((0, database_shard_directory_parts_1.databaseShardDirectoryPartId)(nextIndex++), chunk);
            }
        }
    }
    return parts;
}
/**
 * `shards` in as few even parts as keep each within `targetBytes`. No entry
 * outgrows the hard limit of a directory part.
 */
function splitEvenly(shards, targetBytes) {
    const bytes = shards.map(shard => {
        const size = (0, database_shard_directory_parts_1.databaseShardDirectoryEntryBytes)(shard);
        if (size > database_shard_directory_parts_1.DATABASE_SHARD_DIRECTORY_PART_HARD_LIMIT_BYTES)
            throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard ${shard.shardId} entry exceeds the directory part hard limit`);
        return size;
    });
    const total = bytes.reduce((sum, size) => sum + size, 0);
    if (total <= targetBytes)
        return [shards];
    // Each shard goes to the even share its first byte falls into.
    const count = Math.ceil(total / targetBytes);
    const share = total / count;
    const chunks = Array.from({ length: count }, () => []);
    let start = 0;
    shards.forEach((shard, index) => {
        chunks[Math.min(count - 1, Math.floor(start / share))].push(shard);
        start += bytes[index];
    });
    return chunks.filter(chunk => chunk.length > 0);
}
function requireYMap(value, what) {
    if (!value ||
        typeof value.get !== 'function' ||
        typeof value.set !== 'function')
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`${what} must be a Y.Map`);
    return value;
}
/**
 * The stored head and its parts, each read from its part document. A writer
 * needs the complete directory: a part that is not loaded throws
 * {@link DatabaseShardDirectoryPartsMissingError}. The open last part is
 * always loaded, read or not: its numbers and its highest shard index live
 * only there.
 * `head` is the resolved head; `parts` is the directory a reader sees, every
 * shard once in its first part; `stored` holds the head and the parts as
 * written, so a write diffed against them drops what concurrent writers
 * stored twice.
 */
function readStoredDirectory(storedHead, partSource, 
/** Part documents to read; every part of the head when absent. */
readPartDocIds) {
    const storedForm = (0, database_shard_directory_parts_1.normalizeDatabaseShardDirectoryHead)(storedHead);
    if (!storedForm)
        return undefined;
    const summaries = readPartDocIds
        ? storedForm.parts.filter(summary => readPartDocIds.has((0, database_shard_directory_parts_1.databaseShardDirectoryPartDocId)(storedForm, summary.partId)))
        : storedForm.parts;
    const read = (0, database_shard_directory_parts_1.readDatabaseShardDirectoryParts)(storedForm, partSource, summaries);
    if (read.status === 'loading')
        throw new database_storage_errors_1.DatabaseShardDirectoryPartsMissingError(storedForm.databaseId, read.partDocIds);
    const resolved = (0, database_shard_directory_parts_1.resolveDatabaseShardDirectoryHead)(storedForm, partSource);
    if (resolved.status === 'loading')
        throw new database_storage_errors_1.DatabaseShardDirectoryPartsMissingError(storedForm.databaseId, resolved.partDocIds);
    const head = resolved.head;
    const open = storedForm.parts.at(-1);
    const openRead = open && (0, database_shard_directory_parts_1.isOpenDatabaseShardDirectoryPart)(open)
        ? (0, database_shard_directory_parts_1.readDatabaseShardDirectoryParts)(storedForm, partSource, [open])
        : undefined;
    const openShards = openRead?.status === 'ready' ? openRead.shards : [];
    const storedParts = new Map(summaries.map(summary => [
        summary.partId,
        (0, database_shard_directory_parts_1.readStoredDatabaseShardDirectoryPart)(partSource((0, database_shard_directory_parts_1.databaseShardDirectoryPartDocId)(head, summary.partId))),
    ]));
    const seen = new Set();
    const parts = summaries.map(summary => {
        const part = storedParts.get(summary.partId);
        return {
            ...part,
            shards: part.shards.filter(shard => {
                if (seen.has(shard.shardId))
                    return false;
                seen.add(shard.shardId);
                return true;
            }),
        };
    });
    return {
        head,
        parts,
        openShards,
        stored: {
            head: (0, database_shard_directory_parts_1.readStoredDatabaseShardDirectoryHead)(storedHead),
            parts: storedParts,
        },
    };
}
function writePart(map, part, fields) {
    const stored = (0, database_shard_directory_parts_1.readStoredDatabaseShardDirectoryPart)(map);
    if (!stored) {
        (0, database_shard_directory_parts_1.writeNewDatabaseShardDirectoryPart)(map, part);
        return;
    }
    for (const key of ['databaseId', 'partId'])
        if (stored[key] !== part[key])
            map.set(key, part[key]);
    (0, database_shard_manifest_crdt_1.applyDatabaseShardManifestChanges)(map, (0, database_shard_directory_parts_1.databaseShardDirectoryPartShards)(map), (0, database_shard_manifest_crdt_1.planDatabaseShardManifestChanges)({ ...fields, shards: stored.shards }, { ...fields, shards: part.shards }));
}
/**
 * Fields of a part summary in the head. Writers store a changed summary
 * whole, so concurrent writers merge to one writer's summary.
 */
exports.DATABASE_SHARD_DIRECTORY_PART_SUMMARY_KEYS = [
    'firstShardId',
    'lastShardId',
    'shardCount',
    'rowCount',
];
/** A summary field as stored: none on the open part. */
function storedSummaryField(part, key) {
    return (0, database_shard_directory_parts_1.isOpenDatabaseShardDirectoryPart)(part) ? undefined : part[key];
}
/**
 * Minimal ordered changes from the stored head to `next`, both in stored form
 * (`storedHead` of a planned write), so a write keeps unchanged part
 * summaries as the same CRDT items and leaves the head alone while it only
 * appends into the open part. Hosts apply them to the raw head map
 * ({@link applyDatabaseShardDirectoryHeadChanges}) or through their own
 * reactive wrapper of it.
 */
function planDatabaseShardDirectoryHeadChanges(stored, next) {
    const changes = [];
    const { parts: storedParts, ...storedFields } = stored;
    const { parts: nextParts, ...nextFields } = next;
    for (const [key, value] of Object.entries(nextFields))
        if (storedFields[key] !== value)
            changes.push({ kind: 'field', key, value });
    const order = (0, database_row_shard_planning_1.planDatabaseRowOrderChange)(storedParts.map(part => part.partId), nextParts.map(part => part.partId));
    if (order.deleteCount || order.insert.length)
        changes.push({
            kind: 'parts',
            index: order.index,
            deleteCount: order.deleteCount,
            insert: nextParts.slice(order.index, order.index + order.insert.length),
        });
    // A part id stored twice keeps its first summary; the order change above
    // removed the second.
    const previous = new Map([...storedParts].reverse().map(part => [part.partId, part]));
    const inserted = new Set(order.insert);
    nextParts.forEach((part, index) => {
        if (inserted.has(part.partId))
            return;
        const before = previous.get(part.partId);
        // A changed summary is written whole: concurrent writers of a part then
        // merge to one writer's summary, never to a mix of both.
        if (exports.DATABASE_SHARD_DIRECTORY_PART_SUMMARY_KEYS.some(key => storedSummaryField(before, key) !== storedSummaryField(part, key)))
            for (const key of exports.DATABASE_SHARD_DIRECTORY_PART_SUMMARY_KEYS)
                changes.push({
                    kind: 'part-field',
                    index,
                    key,
                    value: storedSummaryField(part, key),
                });
    });
    return changes;
}
/** Applies head `changes` to the stored head map of a database block. */
function applyDatabaseShardDirectoryHeadChanges(head, changes) {
    const list = head.get('parts');
    for (const change of changes) {
        switch (change.kind) {
            case 'field':
                head.set(change.key, change.value);
                break;
            case 'parts':
                if (change.deleteCount)
                    list.delete(change.index, change.deleteCount);
                if (change.insert.length)
                    list.insert(change.index, change.insert.map(part => new yjs_1.Map(Object.entries(part))));
                break;
            case 'part-field': {
                const summary = requireYMap(list.get(change.index), 'Database directory part summary');
                if (change.value === undefined)
                    summary.delete(change.key);
                else
                    summary.set(change.key, change.value);
                break;
            }
        }
    }
}
/**
 * Plans storing `next` as the directory of the database block: the next head
 * and the part documents whose shards change, read from the stored parts of
 * `partSource`. A missing part throws
 * {@link DatabaseShardDirectoryPartsMissingError} before anything is written.
 *
 * With `readPartDocIds`, `next` holds the shards of only those stored parts, in
 * directory order, and the shards the write adds after them; every other
 * part keeps its shards and its summary and is never read; the open last
 * part is read in either case. Without it, `next` is the complete directory.
 * The head carries the highest shard index of the stored head, of `next` and
 * of its shards; the stored head only that of its closed parts.
 */
function planDatabaseShardDirectoryWrite(storedHead, partSource, next, targetBytes = database_shard_directory_parts_1.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES, readPartDocIds) {
    const stored = readStoredDirectory(storedHead, partSource, readPartDocIds && new Set(readPartDocIds));
    if (readPartDocIds && !stored)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard directory of ${next.databaseId} is not stored; a write of some parts needs its head`);
    if (stored && stored.head.databaseId !== next.databaseId)
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard directory of ${stored.head.databaseId} cannot store ${next.databaseId}`);
    const readParts = new Map(stored?.parts.map(part => [part.partId, part]));
    const planned = planDirectoryParts(readPartDocIds
        ? stored.head.parts.map(summary => readParts.get(summary.partId) ?? { partId: summary.partId })
        : (stored?.parts ?? []), next, targetBytes);
    const storedSummaries = new Map(stored?.head.parts.map(summary => [summary.partId, summary]));
    const { shards: _shards, ...fields } = next;
    const handedOut = Math.max(stored?.head.nextShardIndex ?? 0, (0, database_row_shard_planning_1.nextDatabaseShardIndex)(next));
    const head = {
        ...fields,
        nextShardIndex: handedOut,
        parts: planned.map(part => part.shards
            ? (0, database_shard_directory_parts_1.summarizeDatabaseShardDirectoryPart)(part.partId, part.shards)
            : storedSummaries.get(part.partId)),
    };
    const parts = planned.filter((part) => part.shards !== undefined);
    const storedParts = stored?.stored.parts ?? new Map();
    const partChanges = new Map();
    const changed = parts.filter(part => {
        const before = storedParts.get(part.partId);
        if (!before)
            return true;
        const changes = (0, database_shard_manifest_crdt_1.planDatabaseShardManifestChanges)({ ...fields, shards: before.shards }, { ...fields, shards: part.shards });
        partChanges.set(part, changes);
        return before.databaseId !== part.databaseId || changes.length > 0;
    });
    const partDocId = (part) => (0, database_shard_directory_parts_1.databaseShardDirectoryPartDocId)(head, part.partId);
    // The stored head keeps the highest shard index of its closed parts; the
    // open part's own shards count when it is read. An index the open part no
    // longer holds (its last shards removed) stays in the head.
    const closedShards = planned.slice(0, -1).flatMap(part => part.shards ?? []);
    const last = planned.at(-1);
    const openIndex = (0, database_row_shard_planning_1.nextDatabaseRowShardIndex)(last?.shards ??
        (last && last.partId === stored?.stored.head.parts.at(-1)?.partId
            ? stored.openShards
            : []));
    const storedHeadOfWrite = {
        ...(0, database_shard_directory_parts_1.databaseShardDirectoryStoredHead)(head),
        nextShardIndex: Math.max(stored?.stored.head.nextShardIndex ?? 0, (0, database_row_shard_planning_1.nextDatabaseRowShardIndex)(closedShards), handedOut > openIndex ? handedOut : 0),
    };
    return {
        stored: stored?.stored.head,
        head,
        storedHead: storedHeadOfWrite,
        writtenPartDocIds: changed.map(partDocId),
        writtenParts: changed,
        writtenPartChanges: changed.map(part => partChanges.get(part)),
        writeParts: partMaps => {
            for (const part of changed)
                writePart(partMaps(partDocId(part)), part, fields);
        },
    };
}
/**
 * Stores `next` as the directory of the database block: the head on the block
 * and the shard entries in the part documents of `partMaps`, changing only
 * the parts whose shards differ and only the head fields and part summaries
 * that differ. Callers run it inside their own Yjs transactions and publish
 * the block document and the returned part documents in one publication.
 * A part whose shards are all gone stays in the head as an empty entry and
 * its document keeps the empty part, so its id is reused, never reissued.
 */
function writeDatabaseShardDirectory(block, partMaps, next, targetBytes = database_shard_directory_parts_1.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES) {
    const write = planDatabaseShardDirectoryWrite(block.get(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP), partMaps, next, targetBytes);
    // A stored head that is no map fails before any part changes.
    const storedHead = write.stored &&
        requireYMap(block.get(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP), 'Database shard directory head');
    write.writeParts(partMaps);
    if (write.stored && storedHead)
        applyDatabaseShardDirectoryHeadChanges(storedHead, planDatabaseShardDirectoryHeadChanges(write.stored, write.storedHead));
    else
        block.set(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP, (0, database_shard_directory_parts_1.databaseShardDirectoryHeadToYMap)(write.head));
    return { writtenPartDocIds: write.writtenPartDocIds };
}
/**
 * Stores `next` as the directory of a block that stores none: the head on the
 * block and new part documents, returned by document id. For writers building
 * a database graph, such as imports and provisioning from an archive; they
 * persist the part documents together with the block's document.
 */
function writeNewDatabaseShardDirectory(block, next, targetBytes = database_shard_directory_parts_1.DATABASE_SHARD_DIRECTORY_PART_TARGET_BYTES) {
    if (block.has(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP))
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database shard directory of ${next.databaseId} is already stored`);
    const parts = new Map();
    writeDatabaseShardDirectory(block, partDocId => {
        let part = parts.get(partDocId);
        if (!part) {
            part = new yjs_1.Doc({ guid: partDocId });
            parts.set(partDocId, part);
        }
        return (0, database_shard_directory_parts_1.databaseShardDirectoryPartMap)(part);
    }, next, targetBytes);
    return parts;
}
